import React, { useEffect, useState } from "react";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	MenuItem,
	TextField,
	Tooltip,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import RefreshIcon from "@material-ui/icons/Refresh";
import { getSmsActions, getSmsHistory, sendSmsCommand } from "../../../api/gpsdevice";
import { alertError, alertSuccess } from "../../../utils/logger";

/**
 * Comandos SMS de rescate para un dispositivo (vía API 1NCE).
 * Las acciones vienen del backend (lista blanca: nunca texto libre).
 * Cuota vitalicia de 250 SMS por SIM → confirmación explícita antes de enviar.
 */
export default function SmsCommandDialog({ device, open, onClose }) {
	const [actions, setActions] = useState([]);
	const [action, setAction] = useState("rescue");
	const [seconds, setSeconds] = useState(10);
	const [history, setHistory] = useState(null);
	const [sending, setSending] = useState(false);
	const [confirming, setConfirming] = useState(false);

	useEffect(() => {
		if (!open) return;
		setConfirming(false);
		getSmsActions()
			.then((res) => setActions(res.data || []))
			.catch(() => setActions([]));
		loadHistory();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, device]);

	const loadHistory = () => {
		if (!device?._id) return;
		setHistory(null);
		getSmsHistory(device._id)
			.then((res) => setHistory(res.data.messages || []))
			.catch(() => setHistory([]));
	};

	const handleSend = () => {
		setSending(true);
		sendSmsCommand(device._id, { action, ...(action === "interval" ? { seconds } : {}) })
			.then((res) => {
				alertSuccess({
					title: "SMS enviado",
					customMessage: `"${res.data.payload}" · ${res.data.note || ""}`,
				});
				setConfirming(false);
				setTimeout(loadHistory, 1500);
			})
			.catch((error) =>
				alertError({ error, customMessage: "No se pudo enviar el SMS." })
			)
			.finally(() => setSending(false));
	};

	const selected = actions.find((a) => a.key === action);

	return (
		<Dialog open={!!open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>
				Comandos SMS · {device?.reference || "GPS"} {device?.code}
				<IconButton style={{ position: "absolute", right: 8, top: 8 }} onClick={onClose}>
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent>
				{/*
				  AQUI NO SE DECIDE SI SE PUEDE MANDAR O NO.

				  Se bloqueaba el dialogo cuando el aparato no tenia ICCID guardado, y
				  ese dato solo aparece cuando el propio aparato lo reporta: un aparato
				  recien configurado no lo ha hecho nunca, y es justo el que hay que
				  rescatar por SMS. Salian como <<SIM ajena a 1NCE>> aparatos con una
				  1NCE puesta y funcionando.

				  Ahora se intenta y manda el servidor, que puede preguntarle a 1NCE que
				  SIM lleva cada IMEI. Si de verdad no se sabe, lo dice al enviar y con
				  el motivo de verdad.
				*/}
				{!device?.iccid && (
					<p className="text-muted" style={{ fontSize: 13 }}>
						Este aparato aun no ha reportado su SIM. Se intentara averiguar cual lleva
						consultando a 1NCE por su IMEI.
					</p>
				)}

				<p className="text-muted" style={{ fontSize: 13 }}>
					Cuota de <strong>250 SMS por SIM para toda su vida</strong>: usar solo para
					rescatar dispositivos, nunca de forma rutinaria. En 2G la entrega puede
					tardar varios minutos.
				</p>

				<TextField
					select
					fullWidth
					label="Acción"
					value={action}
					onChange={(e) => {
						setAction(e.target.value);
						setConfirming(false);
					}}
					margin="normal"
				>
					{actions.map((a) => (
						<MenuItem key={a.key} value={a.key}>
							{a.label}
						</MenuItem>
					))}
				</TextField>

				{action === "interval" && (
					<TextField
						type="number"
						fullWidth
						label="Segundos entre posiciones (10–60)"
						value={seconds}
						onChange={(e) => setSeconds(e.target.value)}
						inputProps={{ min: 10, max: 60 }}
						margin="normal"
					/>
				)}

				{selected?.needsServer && (
					<p className="text-muted" style={{ fontSize: 12 }}>
						Reapunta el dispositivo al servidor configurado en el backend
						(GPS_PUBLIC_HOST). Úsalo cuando el semáforo diga “con red móvil pero sin
						conexión al servidor”.
					</p>
				)}

				<div className="d-flex align-items-center justify-content-between mt-4 mb-1">
					<strong style={{ fontSize: 13 }}>Historial</strong>
					<Tooltip title="Actualizar">
						<Button size="small" onClick={loadHistory}>
							<RefreshIcon fontSize="small" />
						</Button>
					</Tooltip>
				</div>
				<div style={{ maxHeight: 180, overflowY: "auto", fontSize: 12 }}>
					{history === null && <span className="text-muted">Cargando…</span>}
					{history?.length === 0 && (
						<span className="text-muted">Sin mensajes todavía.</span>
					)}
					{history?.map((m) => (
						<div
							key={m.id}
							className="d-flex align-items-center py-1"
							style={{ borderBottom: "1px solid #f0f0f0" }}
						>
							<span
								style={{
									fontWeight: 600,
									color: m.direction === "device" ? "#1BC5BD" : "#7E8299",
									minWidth: 70,
								}}
							>
								{m.direction === "device" ? "📩 device" : "→ enviado"}
							</span>
							<code style={{ flex: 1, minWidth: 0 }}>{m.payload}</code>
							<span className="text-muted" style={{ whiteSpace: "nowrap" }}>
								{m.status}
								{m.retries > 0 ? ` (${m.retries})` : ""}
							</span>
						</div>
					))}
				</div>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cerrar</Button>
				{!confirming ? (
					<Button
						color="primary"
						variant="contained"
						disabled={sending}
						onClick={() => setConfirming(true)}
					>
						Enviar comando
					</Button>
				) : (
					<Button
						color="secondary"
						variant="contained"
						disabled={sending}
						onClick={handleSend}
					>
						{sending ? "Enviando…" : "Confirmar envío (gasta 1 SMS)"}
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
}
